import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { ChevronsLeftRight, Maximize2, X } from "lucide-react";

interface Props {
  renderSrc: string;
  realitySrc: string;
  renderAlt: string;
  realityAlt: string;
  materials?: string[];
  duration?: string;
  city?: string;
  budget?: string;
  note?: string;
  idPrefix?: string;
}

export default function RenderRealitySlider({
  renderSrc,
  realitySrc,
  renderAlt,
  realityAlt,
  materials,
  duration,
  city,
  budget,
  note,
  idPrefix = "slider",
}: Props) {
  const [pos, setPos] = useState(50);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const interacted = useRef(false);
  const inView = useInView(containerRef, { once: true, amount: 0.35 });

  useEffect(() => {
    if (!inView || interacted.current) return;
    const controls = animate(50, [66, 34, 50], {
      duration: 3,
      ease: "easeInOut",
      onUpdate: (v) => {
        if (!interacted.current) setPos(v);
      },
    });
    return () => controls.stop();
  }, [inView]);

  const update = (clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos(Math.min(98, Math.max(2, ((clientX - r.left) / r.width) * 100)));
  };

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    interacted.current = true;
    dragging.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (dragging.current) update(e.clientX);
  };
  const stop = () => {
    dragging.current = false;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      interacted.current = true;
      setPos((p) => Math.max(2, p - 4));
    } else if (e.key === "ArrowRight") {
      interacted.current = true;
      setPos((p) => Math.min(98, p + 4));
    }
  };

  const body = (
    <div
      ref={containerRef}
      data-testid={`${idPrefix}-render-reality-slider`}
      className="relative h-full w-full cursor-ew-resize select-none overflow-hidden bg-obsidian-card"
      style={{ touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
    >
      <img
        src={realitySrc}
        alt={realityAlt}
        draggable={false}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={renderSrc}
          alt={renderAlt}
          draggable={false}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>

      <div
        data-testid={`${idPrefix}-slider-label-render`}
        className="absolute left-4 top-4 flex items-center gap-2 bg-obsidian/70 px-3 py-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-300 backdrop-blur-sm"
      >
        <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-blue-400" />
        3D · Approved Render
      </div>
      <div
        data-testid={`${idPrefix}-slider-label-reality`}
        className="absolute right-4 top-4 flex items-center gap-2 bg-obsidian/70 px-3 py-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-300 backdrop-blur-sm"
      >
        <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-emerald-400" />
        Site · Finished Reality
      </div>

      <div
        className="absolute inset-y-0 z-10 w-px bg-gold shadow-[0_0_20px_rgba(197,160,89,0.7)]"
        style={{ left: `${pos}%` }}
      >
        <button
          type="button"
          role="slider"
          aria-label="Drag to compare render and reality"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          data-testid={`${idPrefix}-slider-handle`}
          onKeyDown={onKeyDown}
          className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border border-gold bg-obsidian/90 text-gold shadow-[0_0_30px_rgba(197,160,89,0.45)] backdrop-blur transition-transform duration-200 hover:scale-110"
        >
          <ChevronsLeftRight className="h-5 w-5" />
        </button>
      </div>

      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid={`${idPrefix}-slider-expand-button`}
          aria-label="Open full-screen comparison"
          className="absolute bottom-4 right-4 z-10 flex h-10 w-10 items-center justify-center border border-white/20 bg-obsidian/70 text-zinc-200 backdrop-blur transition-colors hover:border-gold hover:text-gold"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  const meta = (materials || duration || city || budget) && (
    <div className="border border-t-0 border-white/10 bg-obsidian-card/90 p-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px]">
        {city && (
          <span className="text-zinc-300">
            <span className="text-zinc-500">City · </span>
            {city}
          </span>
        )}
        {duration && (
          <span className="text-zinc-300">
            <span className="text-zinc-500">On site · </span>
            {duration}
          </span>
        )}
        {budget && (
          <span className="text-zinc-300">
            <span className="text-zinc-500">Scope · </span>
            {budget}
          </span>
        )}
        {materials && materials.length > 0 && (
          <span className="text-zinc-300">
            <span className="text-zinc-500">Materials · </span>
            {materials.join(", ")}
          </span>
        )}
      </div>
      {note && (
        <p className="mt-3 border-l-2 border-gold/60 pl-4 text-[13px] italic leading-relaxed text-zinc-400">
          {note}
        </p>
      )}
    </div>
  );

  return (
    <>
      <div className="frame-clipped">
        <div className="aspect-[16/10] w-full">{body}</div>
        {meta}
      </div>

      {expanded && (
        <div className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-obsidian/97 p-4 sm:p-8" data-testid={`${idPrefix}-slider-fullscreen`}>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            data-testid={`${idPrefix}-slider-fullscreen-close`}
            aria-label="Close full-screen comparison"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-white/20 text-zinc-200 transition-colors hover:border-gold hover:text-gold"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="w-full max-w-6xl">
            <div className="aspect-[16/10] w-full">{body}</div>
          </div>
          {meta}
        </div>
      )}
    </>
  );
}
